import { ImageOptimized } from '../image/ImageOptimized';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { GradientTypography } from '../gradients/GradientTypography';
import { useTheme } from '@mui/material/styles';
import { useNumberFormatter } from 'src/hooks/useNumberFormatter';

export type StatsProps = {
  stats: { title: string; amount: number }[];
};

function StatsIcons({ icon }: { icon: string }) {
  const icons = {
    'zombies killed': '/assets/images/skull.png',
    competitors: '/assets/images/competitors.png',
    'games played': '/assets/images/controller.png',
    headshots: '/assets/images/headshot.png',
  };

  return (
    <Box sx={{ pr: '8px' }}>
      <ImageOptimized
        src={icons[icon as keyof typeof icons]}
        alt={`${icon}-icon`}
        width="52"
        height="40"
      />
    </Box>
  );
}

export function Stats({ stats }: StatsProps) {
  const theme = useTheme();
  const formatter = useNumberFormatter({
    notation: 'compact',
    maximumSignificantDigits: 3,
  });
  return (
    <Box sx={{ width: 'min(586px, 100%)' }}>
      <GradientTypography
        gradientColor="primary.vertical"
        variant="h5"
        sx={{
          mb: 1,
          ml: 3,
        }}
      >
        STATS
      </GradientTypography>
      <Stack
        sx={{
          gap: '16px',
          flexFlow: 'row wrap',
          justifyContent: 'flex-start',
        }}
      >
        {stats.map(({ title, amount }, i) => {
          return (
            <Box
              key={`stats-${i}`}
              sx={(t) => {
                return {
                  width: 'calc(50% - 8px)',
                  height: '140px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'double 2px transparent',
                  backdropFilter: 'blur(7px)',
                  borderRadius: '10px',
                  backgroundImage: `linear-gradient(${theme.palette.background.elevationSolid[10]}, 
                ${theme.palette.background.elevationSolid[10]}), 
                ${theme.palette.primary.vertical}`,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'content-box, border-box',
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[7],
                  },
                  '& > div': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '& > h6': {
                    textTransform: 'uppercase',
                  },
                  [t.breakpoints.down('xl')]: {
                    backgroundImage: 'unset',
                    backgroundColor: t.palette.background.elevation[10],
                    boxShadow: 'unset',
                    border: 'unset',
                    '&:hover': {
                      boxShadow: 'unset',
                    },
                  },
                };
              }}
            >
              <Box>
                <StatsIcons icon={title.toLowerCase()} />
                <Typography variant="h3">{formatter.format(amount)}</Typography>
              </Box>
              <GradientTypography gradientColor="primary.vertical" variant="h6">
                {title}
              </GradientTypography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
